import {
  Box,
  Button,
  CardHeader,
  CircularProgress,
  Divider,
  InputAdornment,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  TextField,
  Typography,
  styled,
  Theme,
} from '@mui/material';
import { CheckCircle } from '@mui/icons-material';

import { formatMonetaryAmount, formatTaxableText, getPlanEstimations } from '@localstack/services';

import { Discount, OrderContext, Plan, Product, TaxDetails } from '@localstack/types';

import { LoadingFragment } from '../../../../feedback';

import { ProductsList } from '../../ProductsList';

const discountStyles = (theme: Theme) => ({
  color: theme.palette.success.main,
  '& span, & p': {
    color: theme.palette.success.main,
  },
});

const boldStyles = {
  fontWeight: 'bold',
  '& span': {
    fontWeight: 'bold',
  },
};

const DashedDivider = styled(Divider)(({ theme }) => ({
  background: 'transparent',
  borderBottom: `1px dashed ${theme.palette.divider}`,
}));

const DiscountListItem = styled(ListItem)(({ theme }) => discountStyles(theme));
const DiscountListItemSecondaryAction = styled(ListItemSecondaryAction)(({ theme }) => discountStyles(theme));

const AppliedPromoCode = styled(Box)(({ theme }) => ({
  color: theme.palette.success.main,
}));

const BoldListItem = styled(ListItem)(boldStyles);
const BoldListItemSecondaryAction = styled(ListItemSecondaryAction)(boldStyles);

interface PurchaseSummaryProps {
  plan: Plan;
  products: Product[];
  context: OrderContext;
  discount?: Optional<Discount>;
  promotionLoading?: boolean;
  onApplyPromoCode?: (planId: string, code: Optional<string>) => unknown;
  seats: number;
  promoCode: Optional<string>;
  setPromoCode: (code: string | null) => void;
  taxLoading: boolean;
  isAdminMode: boolean;
  tax?: Optional<TaxDetails>;
  hasAppliedPromoCode?: boolean;
}

export const PurchaseSummary = (props: PurchaseSummaryProps): JSX.Element => {
  const {
    plan,
    products,
    context,
    discount,
    promotionLoading,
    onApplyPromoCode,
    seats,
    taxLoading,
    tax,
    promoCode,
    setPromoCode,
    isAdminMode,
    hasAppliedPromoCode,
  } = props;

  const customMrr = context.mrr ? context.mrr * (context.interval ?? plan.interval_months) : null;
  const taxAmount = tax?.tax_amount ?? 0;

  const planFullAmount = customMrr ?? getPlanEstimations(products, context, discount, false);
  const planFormattedFullAmount = formatMonetaryAmount(planFullAmount, plan.currency);

  const planDiscountedAmount = customMrr ?? getPlanEstimations(products, context, discount, true);
  const planFormattedDiscountedAmount = formatMonetaryAmount(planDiscountedAmount, plan.currency);

  const planDiscountedAmountWithTax = customMrr ?? planDiscountedAmount + taxAmount;
  const planFormattedDiscountedAmountWithTax = formatMonetaryAmount(planDiscountedAmountWithTax, plan.currency);

  const planFormattedDiscountAmount = formatMonetaryAmount(planFullAmount - planDiscountedAmount, plan.currency);

  const hasDiscount = planFullAmount !== planDiscountedAmount;

  return (
    <>
      <CardHeader title="Summary" />
      <ProductsList plan={plan} products={products ?? []} context={context} isAdminMode={isAdminMode} />
      <List>
        <DashedDivider />
        <ListItem>
          <ListItemText primary={`${seats} Seat(s)`} />
          <ListItemSecondaryAction>
            <Typography>{planFormattedFullAmount}</Typography>
          </ListItemSecondaryAction>
        </ListItem>

        {/* Promo Code */}
        {hasAppliedPromoCode && (
          <Box display="flex" alignItems="center" justifyContent="space-between" gap={1}>
            <AppliedPromoCode display="flex" alignItems="center" gap={1}>
              <CheckCircle fontSize="small" color="inherit" />
              <Typography color="inherit">{promoCode} promo code applied</Typography>
            </AppliedPromoCode>
            <Button
              size="small"
              onClick={() => {
                setPromoCode(null);
                onApplyPromoCode?.(plan.id, null);
              }}
              disabled={promotionLoading}
            >
              Remove
            </Button>
          </Box>
        )}
        {!hasAppliedPromoCode && (
          <Box display="flex">
            <Box flexGrow={1} mr={1}>
              <TextField
                label="Promo Code"
                variant="outlined"
                fullWidth
                value={promoCode || ''}
                onChange={(e) => setPromoCode(e.target.value || null)}
                InputProps={{
                  ...(promotionLoading && {
                    endAdornment: (
                      <InputAdornment position="start">
                        <CircularProgress size={12} />
                      </InputAdornment>
                    ),
                  }),
                }}
              />
            </Box>
            <Button
              variant="outlined"
              color="primary"
              size="large"
              onClick={() => onApplyPromoCode?.(plan.id, promoCode)}
              disabled={promotionLoading}
            >
              Apply
            </Button>
          </Box>
        )}

        {discount && hasDiscount && (
          <>
            <DiscountListItem>
              <ListItemText primary="Discount" secondary={discount.name} />
              <DiscountListItemSecondaryAction>
                <Typography color="inherit">-{planFormattedDiscountAmount}</Typography>
              </DiscountListItemSecondaryAction>
            </DiscountListItem>
            <DashedDivider />
          </>
        )}
        {tax && (
          <>
            <ListItem>
              <ListItemText primary={<>Subtotal {hasDiscount && <sup>*</sup>}</>} />
              <ListItemSecondaryAction>
                <Typography>{planFormattedDiscountedAmount}</Typography>{' '}
              </ListItemSecondaryAction>
            </ListItem>
            <ListItem>
              <ListItemText primary={`VAT (${tax.tax_rate}%)`} />
              <BoldListItemSecondaryAction>
                <Typography>{formatMonetaryAmount(tax.tax_amount, plan.currency)}</Typography>
              </BoldListItemSecondaryAction>
            </ListItem>
          </>
        )}
        <LoadingFragment loading={taxLoading} variant="list" size={3}>
          <BoldListItem>
            <ListItemText
              primary={
                <>
                  {formatTaxableText('Total', tax, true)} {hasDiscount && <sup>*</sup>}
                </>
              }
            />
            <BoldListItemSecondaryAction>
              <Typography>{planFormattedDiscountedAmountWithTax}</Typography>
            </BoldListItemSecondaryAction>
          </BoldListItem>
        </LoadingFragment>
      </List>
    </>
  );
};
